import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  Modal,
  TabItem,
  Tabs,
  useModals,
  ModalProps,
  Avatar,
  Box,
  Button,
  FontAwesomeIcon,
  ListItem,
  Stack,
  Table,
} from "@skailer-com/uikit";
import { Search } from "@/components/search/Search.tsx";
import { getTextPreview } from "@/helpers/utils.helper.ts";
import { useTemplates } from "@/queries/use-templates.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiTemplate, ApiTemplateType } from "@/types/template.types.ts";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";

export interface TemplateChooseModalProps extends ModalProps {
  onApply: (template: ApiTemplate) => void;
}

export function TemplateEmailChooseModal({ onApply, ...props }: TemplateChooseModalProps) {
  const { close } = useModals();
  const { id } = { ...props };
  const { t } = useTranslation();

  const { data: templates, isLoading } = useTemplates();
  const [searchTerm, setSearchTerm] = useState("");

  const filterTemplates = (templateList: ApiTemplate[] | undefined) => {
    if (!templateList || !searchTerm.trim())
      return templateList?.filter((x) => x.template_type == ApiTemplateType.email);

    const term = searchTerm.toLowerCase();
    return templateList.filter(
      (template) =>
        (template.title.toLowerCase().includes(term) ||
          template.subject?.toLowerCase().includes(term) ||
          template.body.toLowerCase().includes(term)) &&
        template.template_type == ApiTemplateType.email
    );
  };

  const recentTemplates = filterTemplates(templates);
  const publicEmailTemplates = filterTemplates(
    templates?.filter((template) => !template.is_private)
  );
  const privateEmailTemplates = filterTemplates(
    templates?.filter((template) => template.is_private)
  );

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.templates.viewedChooseTemplate, {
      [analyticProperties.source]: "Email",
    });
  });

  const columnHelper = createColumnHelper<ApiTemplate>();
  const columns = useMemo<ColumnDef<ApiTemplate>[]>(() => {
    const inner: DisplayColumnDef<ApiTemplate, unknown>[] = [
      {
        header: t("common:title"),
        cell: (row) => (
          <ListItem
            icon={
              <Avatar
                icon={
                  <FontAwesomeIcon
                    icon={`fa-light ${row.row.original.template_type == ApiTemplateType.email ? "fa-envelope" : "fa-paper-plane"}`}
                  />
                }
              />
            }
            title={row.row.original.title}
            caption={getTextPreview(row.row.original.body, 200)}
            forceShowAvatar={true}
            truncate={true}
            behavior={"card"}
          />
        ),
        ...ColumnSizes.employee,
      },
    ];
    inner.push(
      columnHelper.display({
        id: "actions",
        cell: (cell) => (
          <Stack justify="end" gap="sm" direction="horizontal">
            <Button
              onClick={() => {
                onApply(cell.row.original);
                close(id);
              }}
              variant="secondary"
            >
              {t("common:use")}
            </Button>
          </Stack>
        ),
        ...ColumnSizes.actions,
      })
    );
    return inner;
  }, [templates]);

  return (
    <Modal
      {...props}
      size={"lg"}
      title={t("common:choose_template")}
      withCloser={true}
      closeByEscEnabled={true}
    >
      <Box>
        <Tabs>
          <TabItem title={t("common:recent")}>
            <Search
              onInput={(e) => setSearchTerm(e.currentTarget.value)}
              clearable={true}
              onClear={() => setSearchTerm("")}
              autoFocus={true}
              type={"text"}
              value={searchTerm}
              placeholder={t("common:search")}
            />
            <Table
              stickyLastColumn={true}
              data={recentTemplates || []}
              columns={columns}
              showSkeleton={isLoading}
              notFoundComponent={<EmptyStateNothingFound />}
              emptyStateComponent={<EmptyState />}
            />
          </TabItem>
          <TabItem title={t("core:email_template_my")}>
            <Search
              onInput={(e) => setSearchTerm(e.currentTarget.value)}
              clearable={true}
              onClear={() => setSearchTerm("")}
              type={"text"}
              value={searchTerm}
              placeholder={t("common:search")}
            />
            <Table
              stickyLastColumn={true}
              data={privateEmailTemplates || []}
              columns={columns}
              showSkeleton={isLoading}
              notFoundComponent={<EmptyStateNothingFound />}
              emptyStateComponent={<EmptyState />}
            />
          </TabItem>
          <TabItem title={t("core:email_template_shared")}>
            <Search
              onInput={(e) => setSearchTerm(e.currentTarget.value)}
              clearable={true}
              onClear={() => setSearchTerm("")}
              type={"text"}
              value={searchTerm}
              placeholder={t("common:search")}
            />
            <Table
              stickyLastColumn={true}
              data={publicEmailTemplates || []}
              columns={columns}
              showSkeleton={isLoading}
              notFoundComponent={<EmptyStateNothingFound />}
              emptyStateComponent={<EmptyState />}
            />
          </TabItem>
        </Tabs>
      </Box>
    </Modal>
  );
}
